import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  ThemeProvider,
  Stack,
  Alert,
  Snackbar,
  Typography,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Autocomplete,
  TextField,
  Chip,
  Tooltip,
  Checkbox,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ColDef } from "ag-grid-community";
import { RenderTable } from "../../common/splitview-table/RenderTable";
import { gridTheme } from "../../../utils/tableUtils";
import ButtonComponent from "../../common/button/Button";
import ErrorDialog from "../../common/ErrorDialog/ErrorDialog";
import Download from "../../common/DownloadGrid/Download";
import {
  LocalizationProvider,
  DatePicker as MUIDatePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  getWritebackDatesApi,
  submitRegularWritebackApi,
  WritebackDateRecord,
} from "../../../api/regularWritebackApi";
import { getPlantsApi } from "../../../api/getAdminApi";
import { useMsal } from "@azure/msal-react";
import { useDispatch, useSelector } from "react-redux";
import { setApiCall } from "../../../redux/actions/userSlice";
import {
  setSelectedDropdownValue,
  setSelectedRow,
} from "../../../redux/actions/supportTicket";
import { hasRolePermission } from "../../../constants/roleAccess";
import FilterManagerButton from "../../../components/common/FilterVariant/FilterManagerButton";

const DATE_FORMAT = "YYYY.MM.DD";

dayjs.extend(customParseFormat);

interface WritebackTableRow {
  id: string;
  recordId?: number | string;
  Plant: string;
  "From Date": string;
  "To Date": string;
  "Updated By"?: string;
  "Updated Timestamp"?: string;
}

interface FormState {
  plant: string;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
}

const emptyForm = (): FormState => ({
  plant: "",
  fromDate: null,
  toDate: null,
});

export default function RegularWritebackFlagMaintenance() {
  const theme = useTheme();
  const { accounts } = useMsal();
  const dispatch = useDispatch();
  const gridRef = useRef<any>(null);
  const mountedRef = useRef(true);

  const currentRole = useSelector(
    (state: any) => state?.userRoles?.currentRole ?? null,
  );
  const canEditSsaControl = hasRolePermission(currentRole, "edit_ssa_control");

  const [tableRows, setTableRows] = useState<WritebackTableRow[]>([]);
  const [plants, setPlants] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"add" | "edit">("add");
  const [form, setForm] = useState<FormState>(emptyForm());
  const [formErrors, setFormErrors] = useState<
    Partial<Record<keyof FormState, string>>
  >({});

  // Delete confirm
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingRow, setDeletingRow] = useState<WritebackTableRow | null>(
    null,
  );

  // Toast
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("success");

  const [submitting, setSubmitting] = useState(false);
  const [isGridReady, setIsGridReady] = useState(false);
  const [tableRenderKey, setTableRenderKey] = useState(0);

  // Multi-plant add
  const [addPlants, setAddPlants] = useState<string[]>([]);

  // Row selection for bulk ops
  const [selectedRows, setSelectedRows] = useState<WritebackTableRow[]>([]);

  // Bulk delete
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  // Bulk edit (dates only)
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkFromDate, setBulkFromDate] = useState<Dayjs | null>(null);
  const [bulkToDate, setBulkToDate] = useState<Dayjs | null>(null);
  const [bulkDateErrors, setBulkDateErrors] = useState<{
    fromDate?: string;
    toDate?: string;
  }>({});

  const fitColumnsToWidth = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    try {
      api.sizeColumnsToFit();
    } catch {
      // Grid can briefly be in transition during mount/unmount.
    }
  }, []);

  const getUpdatedBy = (): string => {
    const primary = accounts?.[0] as any;
    return (
      primary?.idTokenClaims?.preferred_username ||
      primary?.username ||
      primary?.idTokenClaims?.email ||
      ""
    );
  };

  const parseDateValue = (value?: string | null): Dayjs | null => {
    const raw = String(value ?? "").trim();
    if (!raw) return null;

    const parsed = [
      dayjs(raw, DATE_FORMAT, true),
      dayjs(raw, "YYYY-MM-DD", true),
      dayjs(raw, "YYYY/MM/DD", true),
      dayjs(raw, "MM/DD/YYYY", true),
      dayjs(raw),
    ].find((d) => d.isValid());

    return parsed ? parsed.startOf("day") : null;
  };

  const formatDateForTable = (value?: string | null): string => {
    const parsed = parseDateValue(value);
    return parsed ? parsed.format(DATE_FORMAT) : String(value ?? "");
  };

  const getSuccessMessageFromResponse = (
    response: any,
    fallback: string,
  ): string => {
    if (typeof response === "string" && response.trim()) return response;
    if (typeof response?.message === "string" && response.message.trim())
      return response.message;
    if (
      typeof response?.data?.message === "string" &&
      response.data.message.trim()
    ) {
      return response.data.message;
    }
    return fallback;
  };

  const toRows = (data: any): WritebackTableRow[] => {
    const records: WritebackDateRecord[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.records)
          ? data.records
          : [];
    return records
      .map((item, idx) => ({
        id: String(item.id ?? `${item.plant ?? ""}__${idx}`),
        recordId: item.id,
        Plant: item.plant ?? "",
        "From Date": formatDateForTable(item.fromDate),
        "To Date": formatDateForTable(item.toDate),
        "Updated By": item.updatedBy ?? "-",
        "Updated Timestamp": item.updatedTimestamp ?? "-",
      }))
      .sort((a, b) => String(a.Plant).localeCompare(String(b.Plant)));
  };

  const fetchData = useCallback(() => {
    mountedRef.current = true;
    setLoading(true);
    dispatch(setApiCall(true));

    Promise.allSettled([
      getWritebackDatesApi().then((data) => {
        if (!mountedRef.current) return;
        setTableRows(toRows(data));
      }),
      getPlantsApi().then((data) => {
        if (mountedRef.current) setPlants(data);
      }),
    ])
      .then((results) => {
        results.forEach((result) => {
          if (result.status === "rejected") {
            console.error("Fetch error:", result.reason);
          }
        });
      })
      .finally(() => {
        if (mountedRef.current) {
          setLoading(false);
          dispatch(setApiCall(false));
        }
      });
  }, [dispatch]);

  useEffect(() => {
    fetchData();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData]);

  const refreshTable = async () => {
    const data = await getWritebackDatesApi();
    if (!mountedRef.current) return;
    setTableRows(toRows(data));
  };

  const remountTable = useCallback(() => {
    setTableRenderKey((prev) => prev + 1);
  }, []);

  // ── Columns ──────────────────────────────────────────────────────────────
  const columns: ColDef[] = useMemo(
    () => [
      {
        headerName: "Plant",
        field: "Plant",
        comparator: (a, b) => String(a ?? "").localeCompare(String(b ?? "")),
        valueFormatter: (p) => p.value ?? "-",
      },
      {
        headerName: "From Date",
        field: "From Date",
        valueFormatter: (p) => p.value ?? "-",
      },
      {
        headerName: "To Date",
        field: "To Date",
        valueFormatter: (p) => p.value ?? "-",
      },
      {
        headerName: "Updated By",
        field: "Updated By",
        valueFormatter: (p) => p.value ?? "-",
        hide: true,
      },
      {
        headerName: "Updated Timestamp",
        field: "Updated Timestamp",
        valueFormatter: (p) => p.value ?? "-",
        hide: true,
      },
      ...(canEditSsaControl
        ? [
            {
              headerName: "Action",
              field: "action",
              sortable: false,
              filter: false,
              cellStyle: {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              },
              cellRenderer: (params: any) => {
                const row: WritebackTableRow = params.data;
                return (
                  <>
                    <IconButton
                      size="small"
                      title="Edit"
                      onClick={() => openEditDialog(row)}
                      sx={{ "&:hover": { color: theme.palette.primary.main } }}
                    >
                      <EditIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      title="Delete"
                      onClick={() => openDeleteDialog(row)}
                      sx={{ "&:hover": { color: theme.palette.error.main } }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </>
                );
              },
            } as ColDef,
          ]
        : []),
    ],
    [theme, canEditSsaControl],
  );

  // ── Dialog helpers ────────────────────────────────────────────────────────
  const openAddDialog = () => {
    setDialogMode("add");
    setForm(emptyForm());
    setFormErrors({});
    setAddPlants([]);
    setDialogOpen(true);
  };

  const findLatestRow = useCallback(
    (row: WritebackTableRow): WritebackTableRow | null => {
      const latest = tableRows.find(
        (item) =>
          item.id === row.id ||
          (item.recordId !== undefined &&
            row.recordId !== undefined &&
            item.recordId === row.recordId),
      );
      return latest ?? null;
    },
    [tableRows],
  );

  const openEditDialog = (row: WritebackTableRow) => {
    const sourceRow = findLatestRow(row) ?? row;
    setDialogMode("edit");
    setForm({
      plant: sourceRow.Plant,
      fromDate: parseDateValue(sourceRow["From Date"]),
      toDate: parseDateValue(sourceRow["To Date"]),
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const openDeleteDialog = (row: WritebackTableRow) => {
    const sourceRow = findLatestRow(row) ?? row;
    setDeletingRow(sourceRow);
    setDeleteDialogOpen(true);
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof FormState, string>> = {};
    if (!form.plant) errors.plant = "Plant is required";
    if (!form.fromDate || !form.fromDate.isValid())
      errors.fromDate = "From Date is required";
    if (!form.toDate || !form.toDate.isValid())
      errors.toDate = "To Date is required";
    if (form.fromDate && form.toDate && form.toDate.isBefore(form.fromDate)) {
      errors.toDate = "To Date must be on or after From Date";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateAddPlants = (): boolean => {
    const errors: Partial<Record<keyof FormState, string>> = {};
    if (addPlants.length === 0) errors.plant = "At least one plant is required";
    if (!form.fromDate || !form.fromDate.isValid())
      errors.fromDate = "From Date is required";
    if (!form.toDate || !form.toDate.isValid())
      errors.toDate = "To Date is required";
    if (form.fromDate && form.toDate && form.toDate.isBefore(form.fromDate)) {
      errors.toDate = "To Date must be on or after From Date";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isAddFormValid =
    addPlants.length > 0 &&
    !!form.fromDate &&
    form.fromDate.isValid() &&
    !!form.toDate &&
    form.toDate.isValid() &&
    !form.toDate.isBefore(form.fromDate);

  const isEditFormValid =
    !!form.plant &&
    !!form.fromDate &&
    form.fromDate.isValid() &&
    !!form.toDate &&
    form.toDate.isValid() &&
    !form.toDate.isBefore(form.fromDate);

  const isDialogSaveDisabled =
    submitting || (dialogMode === "add" ? !isAddFormValid : !isEditFormValid);

  const isBulkEditSaveDisabled =
    submitting ||
    selectedRows.length === 0 ||
    !bulkFromDate ||
    !bulkFromDate.isValid() ||
    !bulkToDate ||
    !bulkToDate.isValid() ||
    bulkToDate.isBefore(bulkFromDate);

  const handleDialogSubmit = async () => {
    if (dialogMode === "add") {
      if (!validateAddPlants()) return;
      setSubmitting(true);
      try {
        const response = await submitRegularWritebackApi(
          addPlants.map((plant) => ({
            plantId: plant,
            fromDate: form.fromDate!.format(DATE_FORMAT),
            toDate: form.toDate!.format(DATE_FORMAT),
            updatedBy: getUpdatedBy(),
            action: "ADD" as const,
          })),
        );
        setToastSeverity("success");
        setToastMessage(
          getSuccessMessageFromResponse(
            response,
            addPlants.length === 1
              ? "Writeback date added successfully!"
              : `${addPlants.length} records added successfully!`,
          ),
        );
        setToastOpen(true);
        setDialogOpen(false);
        clearSelectedCheckboxes();
        await refreshTable();
      } catch (error: any) {
        setToastSeverity("error");
        setToastMessage(
          error?.response?.data?.message ??
            error?.response?.data ??
            "Failed to save record",
        );
        setToastOpen(true);
      } finally {
        setSubmitting(false);
      }
    } else {
      if (!validateForm()) return;
      setSubmitting(true);
      try {
        const response = await submitRegularWritebackApi([
          {
            plantId: form.plant,
            fromDate: form.fromDate!.format(DATE_FORMAT),
            toDate: form.toDate!.format(DATE_FORMAT),
            updatedBy: getUpdatedBy(),
            action: "UPDATE",
          },
        ]);
        setToastSeverity("success");
        setToastMessage(
          getSuccessMessageFromResponse(
            response,
            "Writeback date updated successfully!",
          ),
        );
        setToastOpen(true);
        setDialogOpen(false);
        clearSelectedCheckboxes();
        await refreshTable();
      } catch (error: any) {
        setToastSeverity("error");
        setToastMessage(
          error?.response?.data?.message ??
            error?.response?.data ??
            "Failed to save record",
        );
        setToastOpen(true);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingRow) return;
    setSubmitting(true);
    try {
      const response = await submitRegularWritebackApi([
        {
          plantId: deletingRow.Plant,
          fromDate: deletingRow["From Date"],
          toDate: deletingRow["To Date"],
          updatedBy: getUpdatedBy(),
          action: "DELETE",
        },
      ]);
      setToastSeverity("success");
      setToastMessage(
        getSuccessMessageFromResponse(response, "Record deleted successfully!"),
      );
      setToastOpen(true);
      setDeleteDialogOpen(false);
      setDeletingRow(null);
      clearSelectedCheckboxes();
      await refreshTable();
      remountTable();
    } catch (error: any) {
      setToastSeverity("error");
      setToastMessage(
        error?.response?.data?.message ??
          error?.response?.data ??
          "Failed to delete record",
      );
      setToastOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedRows.length === 0) return;
    setSubmitting(true);
    try {
      const response = await submitRegularWritebackApi(
        selectedRows.map((row) => ({
          plantId: row.Plant,
          fromDate: row["From Date"],
          toDate: row["To Date"],
          updatedBy: getUpdatedBy(),
          action: "DELETE" as const,
        })),
      );
      setToastSeverity("success");
      setToastMessage(
        getSuccessMessageFromResponse(
          response,
          `${selectedRows.length} record(s) deleted successfully!`,
        ),
      );
      setToastOpen(true);
      setBulkDeleteOpen(false);
      clearSelectedCheckboxes();
      await refreshTable();
      remountTable();
    } catch (error: any) {
      setToastSeverity("error");
      setToastMessage(error?.response?.data?.message ?? "Bulk delete failed");
      setToastOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const validateBulkEdit = (): boolean => {
    const errors: { fromDate?: string; toDate?: string } = {};
    if (!bulkFromDate || !bulkFromDate.isValid())
      errors.fromDate = "From Date is required";
    if (!bulkToDate || !bulkToDate.isValid())
      errors.toDate = "To Date is required";
    if (bulkFromDate && bulkToDate && bulkToDate.isBefore(bulkFromDate)) {
      errors.toDate = "To Date must be on or after From Date";
    }
    setBulkDateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBulkEditSubmit = async () => {
    if (!validateBulkEdit() || selectedRows.length === 0) return;
    setSubmitting(true);
    try {
      const response = await submitRegularWritebackApi(
        selectedRows.map((row) => ({
          plantId: row.Plant,
          fromDate: bulkFromDate!.format(DATE_FORMAT),
          toDate: bulkToDate!.format(DATE_FORMAT),
          updatedBy: getUpdatedBy(),
          action: "UPDATE" as const,
        })),
      );
      setToastSeverity("success");
      setToastMessage(
        getSuccessMessageFromResponse(
          response,
          `${selectedRows.length} record(s) updated successfully!`,
        ),
      );
      setToastOpen(true);
      setBulkEditOpen(false);
      clearSelectedCheckboxes();
      await refreshTable();
      remountTable();
    } catch (error: any) {
      setToastSeverity("error");
      setToastMessage(error?.response?.data?.message ?? "Bulk update failed");
      setToastOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const sortedPlants = useMemo(
    () => [...plants].sort((a, b) => String(a).localeCompare(String(b))),
    [plants],
  );

  const handleResetFilters = () => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.setFilterModel(null);
    if (typeof api.setGridOption === "function") {
      api.setGridOption("quickFilterText", "");
    }
    api.onFilterChanged();
  };

  const clearSelectedCheckboxes = () => {
    const api = gridRef.current?.api;
    if (api?.deselectAll) {
      api.deselectAll();
    }
    setSelectedRows([]);
    dispatch(setSelectedRow([]));
    dispatch(setSelectedDropdownValue(null));
  };

  useEffect(() => {
    if (!isGridReady) return;

    const timer = window.setTimeout(() => {
      fitColumnsToWidth();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [isGridReady, columns, tableRows, fitColumnsToWidth]);

  useEffect(() => {
    if (!isGridReady) return;

    const handleResize = () => fitColumnsToWidth();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isGridReady, fitColumnsToWidth]);

  return (
    <ThemeProvider theme={theme}>
      <Stack gap={2} padding={2} sx={{ paddingLeft: 0, flex: 1 }}>
        {/* Header */}
        <Stack
          direction="row"
          gap={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 600,
              textAlign: "left",
              marginLeft: 1,
            }}
          >
            Regular Writeback Flag Maintenance
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {canEditSsaControl && (
              <ButtonComponent
                className="take-action"
                type="secondary"
                label="Add Record"
                onClick={openAddDialog}
                borderRadius="28px"
              />
            )}
            {canEditSsaControl && selectedRows.length > 0 && (
              <>
                <Tooltip title="Edit date range for selected rows">
                  <span>
                    <ButtonComponent
                      className="take-action"
                      type="secondary"
                      label={`Bulk Edit (${selectedRows.length})`}
                      onClick={() => {
                        setBulkFromDate(null);
                        setBulkToDate(null);
                        setBulkDateErrors({});
                        setBulkEditOpen(true);
                      }}
                      borderRadius="28px"
                      width="auto"
                      maxWidth="none"
                    />
                  </span>
                </Tooltip>
                <Tooltip title="Delete selected rows">
                  <span>
                    <ButtonComponent
                      className="take-action"
                      type="secondary"
                      label={`Bulk Delete (${selectedRows.length})`}
                      onClick={() => setBulkDeleteOpen(true)}
                      borderRadius="28px"
                      width="auto"
                      maxWidth="none"
                    />
                  </span>
                </Tooltip>
              </>
            )}
            {(tableRows?.length ?? 0) > 0 && (
              <>
                <ButtonComponent
                  className="take-action"
                  type="secondary"
                  label="Reset Filters"
                  onClick={handleResetFilters}
                  borderRadius="28px"
                  width="auto"
                  maxWidth="none"
                />
                <FilterManagerButton
                  gridRef={gridRef}
                  tableName="Regular Writeback Flag Maintenance"
                  userEmail={localStorage.getItem("userEmail") || ""}
                  disabled={!tableRows?.length}
                />
              </>
            )}
            <Download
              loading={loading}
              gridRef={gridRef}
              fileName="Regular Writeback Flag Maintenance"
              rowData={tableRows}
            />
          </Stack>
        </Stack>

        {/* Add / Edit Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            {dialogMode === "add"
              ? "Add Writeback Date"
              : "Edit Writeback Date"}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {/* Plant — multi-select for Add, single disabled for Edit */}
              {dialogMode === "add" ? (
                <Autocomplete
                  multiple
                  options={sortedPlants}
                  value={addPlants}
                  onChange={(_, val) => {
                    setAddPlants(val);
                    setFormErrors((prev) => ({ ...prev, plant: undefined }));
                  }}
                  disableCloseOnSelect
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        size="small"
                        checked={selected}
                        sx={{ mr: 1, p: 0 }}
                      />
                      {option}
                    </li>
                  )}
                  renderTags={(selected, getTagProps) =>
                    selected.map((option, index) => (
                      <Chip
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Plant(s)"
                      size="small"
                      error={!!formErrors.plant}
                      helperText={
                        formErrors.plant ?? "Select one or more plants"
                      }
                      sx={{
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                          { borderColor: "#000" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
                      }}
                      required
                    />
                  )}
                />
              ) : (
                <Autocomplete
                  options={sortedPlants}
                  value={form.plant || null}
                  onChange={(_, val) => {
                    setForm((prev) => ({ ...prev, plant: val ?? "" }));
                    setFormErrors((prev) => ({ ...prev, plant: undefined }));
                  }}
                  disabled
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Plant"
                      size="small"
                      error={!!formErrors.plant}
                      helperText={formErrors.plant}
                      sx={{
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                          { borderColor: "#000" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
                      }}
                      required
                    />
                  )}
                />
              )}

              {/* From Date */}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MUIDatePicker
                  label="From Date"
                  value={form.fromDate}
                  format={DATE_FORMAT}
                  onChange={(val) => {
                    setForm((prev) => ({ ...prev, fromDate: val }));
                    setFormErrors((prev) => ({ ...prev, fromDate: undefined }));
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      required: true,
                      error: !!formErrors.fromDate,
                      helperText: formErrors.fromDate,
                      sx: {
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                          { borderColor: "#000" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
                      },
                    },
                  }}
                />
              </LocalizationProvider>

              {/* To Date */}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MUIDatePicker
                  label="To Date"
                  value={form.toDate}
                  format={DATE_FORMAT}
                  onChange={(val) => {
                    setForm((prev) => ({ ...prev, toDate: val }));
                    setFormErrors((prev) => ({ ...prev, toDate: undefined }));
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      required: true,
                      error: !!formErrors.toDate,
                      helperText: formErrors.toDate,
                      sx: {
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                          { borderColor: "#000" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <ButtonComponent
              className="take-action"
              type="primary"
              label="Cancel"
              onClick={() => setDialogOpen(false)}
              disabled={submitting}
              borderRadius="28px"
              width="auto"
              maxWidth="none"
            />
            <ButtonComponent
              className="take-action"
              type="secondary"
              label={submitting ? "Saving…" : "Save"}
              onClick={handleDialogSubmit}
              disabled={isDialogSaveDisabled}
              borderRadius="28px"
              width="auto"
              maxWidth="none"
            />
          </DialogActions>
        </Dialog>

        {/* Delete Confirm Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the writeback date for plant{" "}
              <strong>{deletingRow?.Plant}</strong>?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <ButtonComponent
              className="take-action"
              type="primary"
              label="Cancel"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={submitting}
              borderRadius="28px"
              width="auto"
              maxWidth="none"
            />
            <ButtonComponent
              className="take-action"
              type="secondary"
              label={submitting ? "Deleting…" : "Delete"}
              onClick={handleDeleteConfirm}
              disabled={submitting}
              borderRadius="28px"
              width="auto"
              maxWidth="none"
            />
          </DialogActions>
        </Dialog>

        {/* Bulk Delete Confirm Dialog */}
        <Dialog
          open={bulkDeleteOpen}
          onClose={() => setBulkDeleteOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Bulk Delete</DialogTitle>
          <DialogContent>
            <Typography gutterBottom>
              Delete <strong>{selectedRows.length}</strong> selected record(s)?
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
              {selectedRows.map((r) => (
                <Chip key={r.id} label={r.Plant} size="small" />
              ))}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <ButtonComponent
              className="take-action"
              type="primary"
              label="Cancel"
              onClick={() => setBulkDeleteOpen(false)}
              disabled={submitting}
              borderRadius="28px"
              width="auto"
              maxWidth="none"
            />
            <ButtonComponent
              className="take-action"
              type="secondary"
              label={
                submitting
                  ? "Deleting…"
                  : `Delete ${selectedRows.length} Record(s)`
              }
              onClick={handleBulkDeleteConfirm}
              disabled={submitting}
              borderRadius="28px"
              width="auto"
              maxWidth="none"
            />
          </DialogActions>
        </Dialog>

        {/* Bulk Edit Dates Dialog */}
        <Dialog
          open={bulkEditOpen}
          onClose={() => setBulkEditOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Bulk Edit Date Range</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Applying new dates to <strong>{selectedRows.length}</strong>{" "}
              plant(s): {selectedRows.map((r) => r.Plant).join(", ")}
            </Typography>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MUIDatePicker
                  label="From Date"
                  value={bulkFromDate}
                  format={DATE_FORMAT}
                  onChange={(val) => {
                    setBulkFromDate(val);
                    setBulkDateErrors((prev) => ({
                      ...prev,
                      fromDate: undefined,
                    }));
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      required: true,
                      error: !!bulkDateErrors.fromDate,
                      helperText: bulkDateErrors.fromDate,
                      sx: {
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                          { borderColor: "#000" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MUIDatePicker
                  label="To Date"
                  value={bulkToDate}
                  format={DATE_FORMAT}
                  onChange={(val) => {
                    setBulkToDate(val);
                    setBulkDateErrors((prev) => ({
                      ...prev,
                      toDate: undefined,
                    }));
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      required: true,
                      error: !!bulkDateErrors.toDate,
                      helperText: bulkDateErrors.toDate,
                      sx: {
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                          { borderColor: "#000" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <ButtonComponent
              className="take-action"
              type="primary"
              label="Cancel"
              onClick={() => setBulkEditOpen(false)}
              disabled={submitting}
              borderRadius="28px"
              width="auto"
              maxWidth="none"
            />
            <ButtonComponent
              className="take-action"
              type="secondary"
              label={
                submitting
                  ? "Saving…"
                  : `Update ${selectedRows.length} Record(s)`
              }
              onClick={handleBulkEditSubmit}
              disabled={isBulkEditSaveDisabled}
              borderRadius="28px"
              width="auto"
              maxWidth="none"
            />
          </DialogActions>
        </Dialog>

        {/* Error Dialog */}
        <ErrorDialog
          open={toastOpen && toastSeverity === "error"}
          message={toastMessage}
          onClose={() => setToastOpen(false)}
        />

        {/* Success Toast */}
        <Snackbar
          open={toastOpen && toastSeverity !== "error"}
          autoHideDuration={3000}
          onClose={() => setToastOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setToastOpen(false)}
            severity={toastSeverity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {toastMessage}
          </Alert>
        </Snackbar>

        {/* AG Grid Table */}
        <RenderTable
          key={tableRenderKey}
          loading={loading}
          gridRef={gridRef}
          gridTheme={gridTheme}
          columns={columns}
          rows={tableRows}
          rowsPerPage={10}
          setGridReady={() => {
            setIsGridReady(true);
            fitColumnsToWidth();
          }}
          sideBar={{}}
          minimized={null}
          emitEvent={() => {}}
          onChange={() => {}}
          isSelectedRow={canEditSsaControl}
          withSelectionColumn={canEditSsaControl}
          selectionScope="all"
          onSelectionChanged={(e: any) => {
            const rows: WritebackTableRow[] = e.api.getSelectedRows();
            setSelectedRows(rows);
            dispatch(setSelectedRow(rows as any));

            if (rows.length > 0) {
              dispatch(
                setSelectedDropdownValue({
                  menuId: "SSA Control",
                  categoryId: "Data",
                  functionId: "Data",
                }),
              );
            } else {
              dispatch(setSelectedDropdownValue(null));
            }
          }}
          setSelectedRow={() => {}}
        />
      </Stack>
    </ThemeProvider>
  );
}
